import { EventEmitterInterface } from 'pf-frontend-common/src/module/event/emitter.interface';

import { phoneWithoutIdd } from 'common/helper/phone/without-idd';
import { DataStore } from 'common/module/data/store';
import { TelephoneInputCountryInterface } from 'common/module/telephone-input/country.interface';
import { TelephoneInputTemplatePropsInterface } from 'common/module/telephone-input/template-props.interface';
import { TelephoneInputViewOptionsInterface } from 'common/module/telephone-input/view-options.interface';

export class TelephoneInputViewStore extends DataStore {
  /**
   * @inheritDoc
   */
  protected state: TelephoneInputTemplatePropsInterface;

  private options: TelephoneInputViewOptionsInterface;

  /**
   * Constructor
   */
  constructor(eventEmitter: EventEmitterInterface) {
    super(eventEmitter);
  }

  /**
   * @inheritDoc
   */
  public initialize(options: TelephoneInputViewOptionsInterface): void {
    // Cache options
    this.options = options;

    this.setState({
      hasError: false,
      showCountriesList: false,
      placeholder: options.placeholder,
      value: null,
      originalValue: options.value,
      countriesList: options.countriesList,
      onChange: this.onChange,
      onBlur: options.onBlur,
      onClickToggleCountryList: this.onClickToggleCountryList,
      onInput: this.onInput,
      selectedCountry: options.selectedCountry,
      onClickChangeCountryCode: options.onClickChangeCountryCode,
      isLoading: options.isLoading,
    });

    this.setValue();
  }

  /**
   * @inheritDoc
   */
  public getState(): TelephoneInputTemplatePropsInterface {
    return this.state;
  }

  /**
   * Set phone value
   */
  private setValue(): void {
    const phoneNumber = phoneWithoutIdd(this.state.originalValue);
    const country = this.findCountryFromValue(phoneNumber);

    if (country) {
      this.state.selectedCountry = country.countryCode;
      this.state.originalValue = phoneNumber.replace(country.phoneCode, '');
    }

    this.setState({
      ...this.state,
      value: this.getPhoneCode() + this.state.originalValue,
    });
  }

  /**
   * Find country code from phone number
   */
  private findCountryFromValue(phoneNumber: string): TelephoneInputCountryInterface {
    if (!phoneNumber) {
      return;
    }

    // Clear '+' from string
    phoneNumber = phoneNumber.replace('+', '');

    return this.state.countriesList.find((country) => country.phoneCode && phoneNumber.startsWith(country.phoneCode));
  }

  /**
   * Get selected country phone code
   */
  private getPhoneCode(): string {
    const phoneCode = this.state.countriesList.find(
      (country) => country.countryCode === this.state.selectedCountry
    )?.phoneCode;
    return phoneCode ? `+${phoneCode}` : '';
  }

  /**
   * On click selected country show/hide country list
   */
  private onClickToggleCountryList = (): void => {
    if (!this.state.isLoading) {
      this.setState({
        ...this.state,
        showCountriesList: !this.state.showCountriesList,
      });
    }
  };

  /**
   * On enter character or on keypress check country and update it
   */
  private onInput = (e: Event): void => {
    const phoneNumber = phoneWithoutIdd((<HTMLInputElement>e.target).value);
    const country = this.findCountryFromValue(phoneNumber);

    if (!country) {
      return;
    }

    this.state.selectedCountry = country.countryCode;
    this.state.originalValue = phoneNumber.replace(country.phoneCode, '');

    this.setState({
      ...this.state,
      value: this.getPhoneCode() + this.state.originalValue,
    });
  };

  /**
   * On change input handler
   */
  private onChange = (e: Event): void => {
    const value = (<HTMLInputElement>e.target).value;

    // TODO-FE[WEB-4457] - move it to form/view-store
    // Update input value
    (<HTMLInputElement>e.target).value = this.findCountryFromValue(value) ? value : this.getPhoneCode() + value;

    this.options.onChange(e);
  };
}
